import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, Trash2, Trash } from 'lucide-react';

// Componentes reutilizables
import SearchBox from '../components/common/SearchBox';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Servicios
import clientService from '../services/clientService';
import { getClientId } from '../utils/clientUtils';

// Estilos
import '../styles/FormSections.css';

const ClientListPage = () => {
  // Estados básicos
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [filters, setFilters] = useState({
    type: '', // PERSON, COMPANY, ''
    status: '', // active, inactive, ''
    dateFrom: '',
    dateTo: ''
  });
  const [formData, setFormData] = useState({
    clientType: 'PERSON',
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    company: '',
    isActive: true
  });

  // Hooks personalizados
  const { notification, showNotification, hideNotification } = useNotification();
  const navigate = useNavigate();
  
  const queryClient = useQueryClient();

  // Queries automáticos mediante React Query
  const { data: clientsData, isLoading: loading, isError } = useQuery({
    queryKey: ['clients', currentPage, itemsPerPage, searchTerm, filters],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        type: filters.type,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      };

      if (filters.status === 'active') params.isActive = 'true';
      else if (filters.status === 'inactive') params.isActive = 'false';
      else params.isActive = '';

      return await clientService.getClients(params);
    },
    placeholderData: (previousData) => previousData
  });

  const clients = clientsData?.data?.clients?.map(client => ({
    ...client,
    id: client._id?.toString()
  })) || [];

  const totalPages = clientsData?.data?.pagination?.totalPages || 1;
  const totalItems = clientsData?.data?.pagination?.totalItems || 0;

  useEffect(() => {
    if (isError) showNotification('Error al cargar los clientes', 'error');
  }, [isError, showNotification]);

  // Mutations para operaciones CUD
  const createMutation = useMutation({
    mutationFn: (newClient) => clientService.createClient(newClient),
    onSuccess: () => {
      showNotification('Cliente creado exitosamente', 'success');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      closeModal();
    },
    onError: () => showNotification('Error al crear el cliente', 'error')
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, clientData }) => clientService.updateClient(id, clientData),
    onSuccess: () => {
      showNotification('Cliente actualizado exitosamente', 'success');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      closeModal();
    },
    onError: () => showNotification('Error al actualizar el cliente', 'error')
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => clientService.deleteClient(id),
    onSuccess: () => {
      showNotification('Cliente enviado a la papelera exitosamente', 'success');
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: () => showNotification('Error al enviar el cliente a la papelera', 'error')
  });

  // Funciones del modal
  const openCreateModal = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      isActive: true
    });
    setIsEditing(false);
    setEditingClient(null);
    setShowModal(true);
  };

  const openEditModal = (client) => {
    if (client) {
      setFormData({
        clientType: client.clientType || 'PERSON',
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        contactPerson: client.contactPerson || '',
        company: client.company || '',
        isActive: client.isActive !== false
      });
      setIsEditing(true);
      setEditingClient(client);
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditingClient(null);
    setFormData({
      clientType: 'PERSON',
      name: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      company: '',
      isActive: true
    });
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (isEditing && editingClient) {
      const clientId = getClientId(editingClient);
      if (!clientId) {
        showNotification('Error: ID del cliente no válido', 'error');
        return;
      }
      updateMutation.mutate({ id: clientId, clientData: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleMoveToTrash = async (clientId) => {
    if (window.confirm('¿Estás seguro de que quieres enviar este cliente a la papelera?\n\nEl cliente será movido a la papelera y podrás restaurarlo más tarde.\nEsta acción no se puede deshacer automáticamente.')) {
      deleteMutation.mutate(clientId);
    }
  };

  // Ver perfil del cliente
  const handleViewProfile = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Función para normalizar texto (eliminar tildes y convertir a minúsculas)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Eliminar tildes y acentos
  };

  // Los clientes ya vienen filtrados del servidor
  const filteredClients = clients;

  // Manejar búsqueda
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      type: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Cambiar página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Cambiar página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Resetear paginación al filtrar
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Clientes</h1>
          <p className="text-gray-600">
            Administra la información de tus clientes y su estado
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/clients/trash')}
            className="btn btn-outline"
          >
            <Trash size={16} /> Papelera
          </button>
          <button
            onClick={openCreateModal}
            className="btn btn-primary"
          >
            + Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Estadísticas de Clientes */}
      <div className="card mb-8">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Estadísticas de Clientes</h2>
        </div>
        <div className="card-content">
          <div className="flex gap-4 justify-center">
            {/* Card Total */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#dbeafe', 
                borderColor: '#93c5fd',
                color: '#1e40af'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#1d4ed8' }}>Clientes Totales</div>
                  <div className="text-xl font-bold" style={{ color: '#1e3a8a' }}>{clients?.length || 0}</div>
                </div>
                <div className="text-2xl">👥</div>
              </div>
            </div>

            {/* Card Activos */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#dcfce7', 
                borderColor: '#86efac',
                color: '#166534'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#15803d' }}>Clientes Activos</div>
                  <div className="text-xl font-bold" style={{ color: '#14532d' }}>
                    {clients?.filter(client => client && client.isActive)?.length || 0}
                  </div>
                </div>
                <div className="text-2xl">✅</div>
              </div>
            </div>

            {/* Card Inactivos */}
            <div 
              className="rounded-lg p-4 w-48 border"
              style={{ 
                backgroundColor: '#fed7aa', 
                borderColor: '#fdba74',
                color: '#c2410c'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium" style={{ color: '#ea580c' }}>Clientes Inactivos</div>
                  <div className="text-xl font-bold" style={{ color: '#9a3412' }}>
                    {clients?.filter(client => client && !client.isActive)?.length || 0}
                  </div>
                </div>
                <div className="text-2xl">⏸️</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div>
        {/* Contenedor de filtros */}
        <div className="card mb-6 filters-container">
          <div className="card-content">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Campo de búsqueda */}
              <div className="flex-1 min-w-64">
                <label className="form-label">Buscar</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Nombre, email o teléfono..."
                  className="form-input"
                />
              </div>

            {/* Filtro por tipo */}
            <div className="w-48">
              <CustomSelect
                label="Tipo"
                value={filters.type}
                onChange={(value) => handleFilterChange('type', value)}
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'PROSPECT', label: 'Cliente Prospecto (Web)' },
                  { value: 'PERSON', label: 'Persona Natural' },
                  { value: 'COMPANY', label: 'Empresa' }
                ]}
                placeholder="Todos los tipos"
              />
            </div>

            {/* Filtro por estado */}
            <div className="w-48">
              <CustomSelect
                label="Estado"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'active', label: 'Activo' },
                  { value: 'inactive', label: 'Inactivo' }
                ]}
                placeholder="Todos los estados"
              />
            </div>

              {/* Filtro por fecha desde */}
              <div className="w-48">
                <label className="form-label">Fecha desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="form-input"
                />
              </div>

              {/* Filtro por fecha hasta */}
              <div className="w-48">
                <label className="form-label">Fecha hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="form-input"
                />
              </div>

              {/* Botón limpiar */}
              <div className="w-48">
                <label className="form-label">&nbsp;</label>
                <button
                  onClick={clearFilters}
                  className="btn btn-outline w-full"
                >
                  🗑️ Limpiar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Indicador de resultados */}
        {(searchTerm || filters.type || filters.status || filters.dateFrom || filters.dateTo) && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              {searching ? 'Buscando...' : `Se encontraron ${filteredClients.length} resultado(s)`}
              {searchTerm && ` para "${searchTerm}"`}
              {filters.type && ` | Tipo: ${filters.type === 'PERSON' ? 'Persona Natural' : 'Empresa'}`}
              {filters.status && ` | Estado: ${filters.status === 'active' ? 'Activo' : 'Inactivo'}`}
              {filters.dateFrom && ` | Desde: ${new Date(filters.dateFrom).toLocaleDateString()}`}
              {filters.dateTo && ` | Hasta: ${new Date(filters.dateTo).toLocaleDateString()}`}
            </p>
          </div>
        )}

        {/* Selector de registros por página */}
        <div className="records-per-page">
          <label htmlFor="itemsPerPage">Registros por página:</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        {/* Tabla de clientes */}
        <div className="table-container">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="loading-spinner"></div>
              <p className="mt-4 text-gray-600">Cargando clientes...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {(searchTerm || filters.type || filters.status || filters.dateFrom || filters.dateTo) ? 'No se encontraron resultados' : 'No hay clientes'}
              </h3>
              <p className="text-gray-600 mb-6">
                {(searchTerm || filters.type || filters.status || filters.dateFrom || filters.dateTo) ? 
                  'No hay clientes que coincidan con los filtros aplicados' : 
                  'Comienza agregando tu primer cliente'
                }
              </p>
              {!(searchTerm || filters.type || filters.status || filters.dateFrom || filters.dateTo) && (
                <button
                  onClick={openCreateModal}
                  className="btn btn-primary"
                >
                  + Agregar Cliente
                </button>
              )}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Tipo</th>
                  <th>Fecha de Creación</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={getClientId(client)}>
                    <td>
                      <div className="font-medium text-gray-900">{client.name}</div>
                    </td>
                    <td className="text-sm text-gray-700">{client.email}</td>
                    <td className="text-sm text-gray-700">{client.phone || 'N/A'}</td>
                    <td>
                      <span className={`badge ${client.clientType === 'COMPANY' ? 'badge-primary' : (client.clientType === 'PROSPECT' ? 'badge-warning' : 'badge-secondary')}`}>
                        {client.clientType === 'COMPANY' ? 'Empresa' : (client.clientType === 'PROSPECT' ? 'Cliente Prospecto' : 'Persona Natural')}
                      </span>
                    </td>
                    <td className="text-sm text-gray-700">
                      {new Date(client.createdAt).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td>
                      <span className={`badge ${client.isActive ? 'badge-success' : 'badge-secondary'}`}>
                        {client.isActive ? 'ACTIVO' : 'INACTIVO'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewProfile(getClientId(client))}
                          className="btn btn-primary btn-sm"
                          title="Ver perfil"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => openEditModal(client)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveToTrash(getClientId(client))}
                          className="btn btn-danger btn-sm"
                          title="Enviar a papelera"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} clientes
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Modal de creación/edición */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={isEditing ? 'Editar Cliente' : 'Crear Cliente'}
        onSubmit={handleSubmit}
        submitText={isEditing ? 'Actualizar' : 'Crear'}
      >
        {/* Bloque 1: Tipo de Cliente */}
        <div className="form-section">
          <h4 className="form-section-title">Tipo de Cliente</h4>
          <CustomSelect
            label="Tipo de Cliente"
            value={formData.clientType}
            onChange={(value) => updateFormField('clientType', value)}
            options={[
              { value: 'PERSON', label: 'Persona Natural' },
              { value: 'COMPANY', label: 'Empresa' }
            ]}
            placeholder="Seleccionar tipo de cliente"
          />
        </div>

        {/* Bloque 2: Información Básica */}
        <div className="form-section">
          <h4 className="form-section-title">Información Básica</h4>
          <ModalFormGrid>
            <ModalFormColumn>
              <FormGroup label="Nombre del cliente" required htmlFor="name">
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormField('name', e.target.value)}
                  required
                  className="form-input"
                  placeholder={formData.clientType === 'PERSON' ? "Ej. Juan Pérez" : "Ej. Mi Empresa S.A.S."}
                />
              </FormGroup>

              <FormGroup label="Email" required htmlFor="email">
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => updateFormField('email', e.target.value)}
                  required
                  className="form-input"
                  placeholder="juan@empresa.com"
                />
              </FormGroup>
            </ModalFormColumn>

            <ModalFormColumn>
              <FormGroup label="Teléfono" htmlFor="phone">
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => updateFormField('phone', e.target.value)}
                  className="form-input"
                  placeholder="+57 300 123 4567"
                />
              </FormGroup>
            </ModalFormColumn>
          </ModalFormGrid>
        </div>

        {/* Bloque 3: Información de Empresa (solo si es empresa) */}
        {formData.clientType === 'COMPANY' && (
          <div className="form-section">
            <h4 className="form-section-title">Información de Empresa</h4>
            <FormGroup label="Persona de contacto" htmlFor="contactPerson">
              <input
                type="text"
                id="contactPerson"
                value={formData.contactPerson}
                onChange={(e) => updateFormField('contactPerson', e.target.value)}
                className="form-input"
                placeholder="Nombre del contacto principal"
              />
            </FormGroup>
          </div>
        )}

        {/* Bloque 4: Información Adicional */}
        <div className="form-section">
          <h4 className="form-section-title">Información Adicional</h4>
          <FormGroup label="Dirección" htmlFor="address">
            <textarea
              id="address"
              value={formData.address}
              onChange={(e) => updateFormField('address', e.target.value)}
              className="form-input form-textarea"
              rows="3"
              placeholder="Dirección completa..."
            />
          </FormGroup>
        </div>

        {/* Bloque 5: Estado del Cliente */}
        <div className="form-section">
          <h4 className="form-section-title">Estado del Cliente</h4>
          <CustomSelect
            label="Estado"
            value={formData.isActive}
            onChange={(value) => updateFormField('isActive', value === 'true')}
            options={[
              { value: 'true', label: 'Activo' },
              { value: 'false', label: 'Inactivo' }
            ]}
            placeholder="Seleccionar estado"
          />
        </div>
      </Modal>

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default ClientListPage;